/**
 * ChartEngine — provider-agnostic wrapper around the underlying chart
 * library. Today: TradingView Lightweight Charts. Tomorrow: whatever
 * we want. The rest of the Crypto workspace (Watchlist, IndicatorPanel,
 * DrawingTools, AIOverlay, AlertsPanel) speaks ONLY to this interface,
 * never to the raw provider.
 *
 * Contract (public API)
 * ─────────────────────
 * class ChartEngine {
 *   constructor(container, opts)      — mount into a DOM node
 *   destroy()                          — tear down
 *   setData(candles)                   — full replace of price series
 *   updateLast(candle)                 — patch last candle (live stream)
 *   setChartType('candles'|'line'|'area'|'heikin_ashi')
 *   setTimeframeLabel(label)           — pure cosmetic (title / watermark)
 *   addPlugin(plugin)                  — register a chart plugin
 *   removePlugin(plugin)
 *   resize(w, h)
 *   snapshotDataUrl()                  — PNG data-url for export
 * }
 *
 * Plugin contract (any object implementing these hooks)
 * ─────────────────────────────────────────────────────
 * plugin.name                          — string (unique)
 * plugin.attach(engine)                — engine.provider().* is exposed
 * plugin.onData(candles)               — full replace notification
 * plugin.onUpdate(lastCandle)          — live tick notification
 * plugin.detach()                      — cleanup
 *
 * The engine calls provider.getRaw() only from within plugins so we
 * can swap providers without changing plugins that just want to read
 * OHLC data. Plugins that need to draw on the chart request a
 * *provider capability* (e.g. `engine.capability('lineSeries')`) and
 * get back a stable, provider-agnostic handle.
 */

import { LightweightChartsProvider } from './providers/lightweightChartsProvider';

const PROVIDERS = {
  lightweight: LightweightChartsProvider,
};

export class ChartEngine {
  constructor(container, opts = {}) {
    const providerName = opts.provider || 'lightweight';
    const Provider = PROVIDERS[providerName];
    if (!Provider) throw new Error(`ChartEngine: unknown provider "${providerName}"`);
    this.container = container;
    this.opts = opts;
    this.provider = new Provider(container, opts);
    this.plugins = new Map();      // name -> plugin
    this.candles = [];
    this.type = opts.type || 'candles';
    this.provider.setChartType(this.type);
  }

  // ── Data ──────────────────────────────────────────────────────────
  setData(candles) {
    this.candles = candles || [];
    this.provider.setData(this.candles);
    for (const p of this.plugins.values()) {
      try { p.onData?.(this.candles); } catch (e) { console.warn('plugin.onData failed', p.name, e); }
    }
  }

  updateLast(candle) {
    if (!candle) return;
    // patch or append in the local mirror so plugins can compute
    // incremental indicators over the same series the chart shows.
    const last = this.candles[this.candles.length - 1];
    if (last && last.time === candle.time) {
      this.candles[this.candles.length - 1] = candle;
    } else {
      this.candles.push(candle);
    }
    this.provider.updateLast(candle);
    for (const p of this.plugins.values()) {
      try { p.onUpdate?.(candle, this.candles); } catch (e) { console.warn('plugin.onUpdate failed', p.name, e); }
    }
  }

  // ── Chart config ─────────────────────────────────────────────────
  setChartType(type) {
    if (this.type === type) return;
    this.type = type;
    this.provider.setChartType(type);
    // Replay data so the type change re-derives (e.g. Heikin Ashi).
    if (this.candles.length) this.provider.setData(this.candles);
  }

  setPan2D(on) {
    this.provider.setPan2D?.(!!on);
  }

  updateLive(candle) {
    // Alias for updateLast so external streaming code has a semantic name.
    return this.updateLast(candle);
  }

  setTimeframeLabel(label) {
    this.provider.setTimeframeLabel?.(label);
  }

  resize(w, h) {
    this.provider.resize(w, h);
  }

  // ── Plugin API ───────────────────────────────────────────────────
  addPlugin(plugin) {
    if (!plugin?.name) throw new Error('ChartEngine: plugin missing .name');
    if (this.plugins.has(plugin.name)) this.removePlugin(plugin.name);
    this.plugins.set(plugin.name, plugin);
    try {
      plugin.attach?.(this);
      if (this.candles.length) plugin.onData?.(this.candles);
    } catch (e) {
      console.warn(`plugin "${plugin.name}" attach failed`, e);
      this.plugins.delete(plugin.name);
      return null;
    }
    return plugin;
  }

  removePlugin(nameOrPlugin) {
    const name = typeof nameOrPlugin === 'string' ? nameOrPlugin : nameOrPlugin?.name;
    if (!name) return;
    const p = this.plugins.get(name);
    if (p) {
      try { p.detach?.(); } catch (e) { console.warn('plugin.detach failed', name, e); }
      this.plugins.delete(name);
    }
  }

  getPlugin(name) { return this.plugins.get(name); }
  listPlugins() { return [...this.plugins.keys()]; }

  // ── Provider capabilities (used by plugins) ─────────────────────
  capability(name, opts = {}) {
    return this.provider.capability?.(name, opts);
  }

  releaseCapability(handle) {
    return this.provider.releaseCapability?.(handle);
  }

  // ── Utilities ────────────────────────────────────────────────────
  snapshotDataUrl() {
    return this.provider.snapshotDataUrl?.();
  }

  destroy() {
    // Detach plugins first so they release any capability handles.
    for (const name of [...this.plugins.keys()]) this.removePlugin(name);
    try { this.provider.destroy?.(); } catch { /* noop */ }
    this.provider = null;
    this.candles = [];
  }
}

export default ChartEngine;
